import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../store/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Wine, AlertCircle, Eye, EyeOff } from "lucide-react";
import { USE_MOCK } from "../../api/axiosClient";
import { toast } from "sonner";
import { ROLE_LANDING } from "../../constants/auth";

const demoRoles = [
  { name: "Owner", username: "owner", password: "owner" },
  { name: "Admin", username: "admin", password: "admin" },
  { name: "Cashier", username: "cashier", password: "cashier" },
];

/**
 * Branded login screen with bar/hotel-themed hero image,
 * show/hide password, role-based redirect, and demo role buttons
 * when REACT_APP_USE_MOCK=true.
 */
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1543007630-9710e4a00a20?auto=format&fit=crop&w=1600&q=70";

export const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const { login, loading } = useAuth();
  const navigate = useNavigate();

  const submit = async (u, p) => {
    setErrorMsg(null);

    try {
      const user = await login(u ?? username, p ?? password);

      toast.success(`Welcome, ${user.fullName}`);

      const fallback = ROLE_LANDING[user.roleName] || "/admin/dashboard";
      // A saved URL may be role scoped or restricted to a narrower role
      // (for example, /admin/users is ADMIN only). Start at a known valid page.
      navigate(fallback, { replace: true });
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      <div className="relative lg:w-1/2 h-56 lg:h-auto overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Bar interior with bottles and ambient lighting"
          loading="eager"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-br from-navy-900/85 via-navy-800/70 to-navy-700/60" />

        <div className="relative z-10 h-full flex flex-col justify-between p-6 lg:p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 lg:h-12 lg:w-12 rounded-2xl bg-gold-500 text-navy-900 grid place-items-center font-bold font-display text-lg lg:text-xl">
              THA
            </div>

            <div className="font-display font-bold text-lg lg:text-xl">
              THURULIYA POS
            </div>
          </div>

          <div className="hidden lg:block">
            <p className="text-gold-300 text-xs uppercase tracking-widest font-semibold">
              Point of Sale Management System
            </p>

            <h1 className="font-display text-4xl xl:text-5xl font-extrabold mt-3 leading-tight">
              Manage sales and operations from one reliable dashboard.
            </h1>

            <p className="mt-4 text-white/75 max-w-md">
              Track sales, manage cashier sessions, and oversee BAR and
              Restaurant operations.
            </p>
          </div>

          <div className="hidden lg:block text-white/55 text-xs">
            v2.0 · By Kreshan Jayamanna
          </div>
        </div>
      </div>

      <div className="lg:w-1/2 flex items-center justify-center p-6 md:p-10 lg:p-14">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-widest font-semibold mb-2">
            <Wine className="h-3.5 w-3.5" />
            Sign in
          </div>

          <h2 className="font-display text-3xl font-bold text-slate-900">
            Welcome back
          </h2>

          <p className="text-sm text-slate-500 mt-1">
            {USE_MOCK
              ? "Try a demo role below or use your own credentials."
              : "Sign in with your account."}
          </p>

          <form
            className="mt-8 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div>
              <Label className="text-xs font-medium text-slate-600">
                Username
              </Label>

              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. Prabath"
                autoComplete="username"
                required
                data-testid="login-username"
              />
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600">
                Password
              </Label>

              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••"
                  autoComplete="current-password"
                  required
                  data-testid="login-password"
                  className="pr-11"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-navy-700 transition-colors"
                  data-testid="login-toggle-password"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {errorMsg && (
              <div
                className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                data-testid="login-error"
                role="alert"
              >
                <AlertCircle className="h-4 w-4" />
                {errorMsg}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-navy-800 hover:bg-navy-900 text-white h-11"
              data-testid="login-submit"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          {USE_MOCK && (
            <>
              <p className="mt-8 text-xs font-semibold text-slate-500 uppercase tracking-widest">
                Try a role (demo)
              </p>

              <div className="grid grid-cols-3 gap-2 mt-3">
                {demoRoles.map((r) => (
                  <button
                    key={r.username}
                    type="button"
                    onClick={() => submit(r.username, r.password)}
                    className="text-left px-3 py-3 border border-slate-200 rounded-lg hover:border-navy-400 hover:bg-slate-50 transition-colors"
                    data-testid={`demo-${r.username}`}
                  >
                    <div className="text-sm font-semibold text-slate-900">
                      {r.name}
                    </div>

                    <div className="text-[11px] text-slate-500">
                      {r.username} · {r.password}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
